import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, LabelList } from 'recharts';
import { TrendingUp, Target, ArrowRight, Sparkles, ChevronDown, Wallet, Shield, Home, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { WhatIfSlider } from './WhatIfSlider';
import { LeadCaptureForm } from './LeadCaptureForm';

const API = process.env.REACT_APP_BACKEND_URL;

// German number formatting
const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

// Color palette matching homepage
const COLORS = {
  navy: '#1B2236',
  emerald: '#10B981',
  blue: '#3B82F6',
  amber: '#F59E0B',
  slate: '#64748B',
  purple: '#8B5CF6',
};

// ============================================
// SECTION 1: HERO - Current Result
// ============================================
const HeroSection = ({ totalPension, projectedCapital }) => (
  <motion.section 
    className="text-center py-12 md:py-16"
    initial="initial"
    animate="animate"
    variants={fadeInUp}
    data-testid="hero-section"
  >
    {/* Decorative gradient background */}
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald/5 via-blue/5 to-purple/5 rounded-3xl -mx-4" />
      <div className="relative py-12">
        <p className="text-sm font-medium text-emerald uppercase tracking-widest mb-4">
          Ihre mögliche monatliche Rente
        </p>
        <h1 className="font-outfit text-6xl md:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-navy via-blue-600 to-emerald bg-clip-text text-transparent tracking-tight tabular-nums mb-6">
          {formatCurrency(totalPension)} €
        </h1>
        <p className="text-lg text-slate-500 mb-8">
          Basierend auf Ihrer aktuellen Sparstrategie
        </p>
        <div className="inline-flex items-center gap-6 px-6 py-3 bg-white rounded-full shadow-lg shadow-slate-200/50 text-sm text-slate-600">
          <span>Kapital: <strong className="text-navy">{formatCurrency(projectedCapital)} €</strong></span>
          <span className="w-px h-4 bg-slate-200" />
          <span>Entnahme: <strong className="text-emerald">3,5 %</strong>/Jahr</span>
        </div>
      </div>
    </div>
  </motion.section>
);

// ============================================
// SECTION 2: Target Comparison
// ============================================
const TargetComparisonSection = ({ targetPension, currentPension, achievementPercentage }) => {
  const isGoalMet = currentPension >= targetPension;
  const progressWidth = Math.min(achievementPercentage, 150);
  
  return (
    <motion.section 
      className="py-12 md:py-16"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: "-100px" }}
      variants={fadeInUp}
      data-testid="target-comparison-section"
    >
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-8 md:p-12 border border-slate-100">
        <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-navy mb-10 text-center">
          Zielvergleich
        </h2>
        
        <div className="grid grid-cols-2 gap-8 mb-10">
          <div className="text-center p-6 bg-slate-50 rounded-2xl">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">Gewünschte Rente</p>
            <p className="font-outfit text-3xl md:text-4xl font-bold text-slate-400 tabular-nums">
              {formatCurrency(targetPension)} €
            </p>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-emerald/10 to-blue/10 rounded-2xl border-2 border-emerald/20">
            <p className="text-sm font-medium text-emerald uppercase tracking-wider mb-2">Mögliche Rente</p>
            <p className="font-outfit text-3xl md:text-4xl font-bold text-navy tabular-nums">
              {formatCurrency(currentPension)} €
            </p>
          </div>
        </div>
        
        {/* Progress bar with gradient */}
        <div className="relative h-5 bg-slate-100 rounded-full overflow-hidden mb-6">
          <motion.div 
            className={`absolute inset-y-0 left-0 rounded-full ${
              isGoalMet 
                ? 'bg-gradient-to-r from-emerald to-teal-400' 
                : 'bg-gradient-to-r from-amber-400 to-orange-500'
            }`}
            initial={{ width: 0 }}
            whileInView={{ width: `${Math.min(progressWidth, 100)}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
          />
        </div>
        
        <p className={`text-center text-xl font-semibold ${isGoalMet ? 'text-emerald' : 'text-amber-500'}`}>
          {achievementPercentage.toFixed(0)} % Ihres Rentenziels {isGoalMet ? 'erreicht' : 'auf dem Weg'}
        </p>
      </div>
    </motion.section>
  );
};

// ============================================
// SECTION 3: Strategy Potential (THE AHA MOMENT)
// ============================================
const StrategyPotentialSection = ({ currentPension, taxOptimizedPension, coordinatedPension, currentCapital }) => {
  const monthlyDifference = coordinatedPension - currentPension;
  const yearlyDifference = monthlyDifference * 12;
  
  // Calculate capital for each strategy (matching the chart multipliers)
  const taxOptimizedCapital = Math.round(currentCapital * 1.22);  // +22% like in chart
  const coordinatedCapital = Math.round(currentCapital * 1.45);   // +45% like in chart
  const capitalDifference = coordinatedCapital - currentCapital;
  
  const strategies = [
    {
      id: 'current',
      label: 'Heutige Strategie',
      pension: currentPension,
      capital: currentCapital,
      color: COLORS.slate,
      colorClass: 'bg-slate-500',
      highlighted: false,
    },
    {
      id: 'tax-optimized',
      label: 'Steueroptimiert',
      pension: taxOptimizedPension,
      capital: taxOptimizedCapital,
      color: COLORS.blue,
      colorClass: 'bg-blue-500',
      highlighted: false,
    },
    {
      id: 'coordinated',
      label: 'Koordiniert',
      pension: coordinatedPension,
      capital: coordinatedCapital,
      color: COLORS.emerald,
      colorClass: 'bg-emerald-500',
      highlighted: true,
    },
  ];

  return (
    <motion.section 
      className="py-12 md:py-16"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: "-100px" }}
      variants={fadeInUp}
      data-testid="strategy-potential-section"
    >
      <div className="text-center mb-10">
        <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-navy mb-4">
          Was eine optimierte Strategie verändern kann
        </h2>
        <p className="text-slate-500 text-lg">
          Drei Wege zum gleichen Ziel – mit unterschiedlichen Ergebnissen
        </p>
      </div>
      
      {/* Dual View: Capital + Pension */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Left: End Capital */}
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 md:p-8 border border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-navy/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-navy" />
            </div>
            <div>
              <h3 className="font-semibold text-navy">Endkapital</h3>
              <p className="text-xs text-slate-500">bei Renteneintritt</p>
            </div>
          </div>
          
          {/* Heutige Strategie */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                <span className="text-sm text-slate-600">Heutige Strategie</span>
              </div>
              <span className="font-semibold tabular-nums text-navy">
                {formatCurrency(currentCapital)} €
              </span>
            </div>
            <div className="w-full h-4 bg-slate-200 rounded-full">
              <div className="h-4 rounded-full bg-gray-500" style={{ width: `${Math.round((currentCapital / coordinatedCapital) * 100)}%` }}></div>
            </div>
          </div>
          
          {/* Steueroptimiert */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                <span className="text-sm text-slate-600">Steueroptimiert</span>
              </div>
              <span className="font-semibold tabular-nums text-navy">
                {formatCurrency(taxOptimizedCapital)} €
              </span>
            </div>
            <div className="w-full h-4 bg-slate-200 rounded-full">
              <div className="h-4 rounded-full bg-blue-500" style={{ width: `${Math.round((taxOptimizedCapital / coordinatedCapital) * 100)}%` }}></div>
            </div>
          </div>
          
          {/* Koordiniert */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald"></span>
                <span className="text-sm text-slate-600">Koordiniert</span>
              </div>
              <span className="font-semibold tabular-nums text-emerald text-lg">
                {formatCurrency(coordinatedCapital)} €
              </span>
            </div>
            <div className="w-full h-4 bg-slate-200 rounded-full">
              <div className="h-4 rounded-full bg-emerald" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>

        {/* Right: Monthly Pension */}
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 md:p-8 border border-slate-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald" />
            </div>
            <div>
              <h3 className="font-semibold text-navy">Monatliche Rente</h3>
              <p className="text-xs text-slate-500">zusätzlich zur gesetzlichen</p>
            </div>
          </div>
          
          {/* Heutige Strategie */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                <span className="text-sm text-slate-600">Heutige Strategie</span>
              </div>
              <span className="font-semibold tabular-nums text-navy">
                {formatCurrency(currentPension)} €/M
              </span>
            </div>
            <div className="w-full h-4 bg-slate-200 rounded-full">
              <div className="h-4 rounded-full bg-gray-500" style={{ width: `${Math.round((currentPension / coordinatedPension) * 100)}%` }}></div>
            </div>
          </div>
          
          {/* Steueroptimiert */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                <span className="text-sm text-slate-600">Steueroptimiert</span>
              </div>
              <span className="font-semibold tabular-nums text-navy">
                {formatCurrency(taxOptimizedPension)} €/M
              </span>
            </div>
            <div className="w-full h-4 bg-slate-200 rounded-full">
              <div className="h-4 rounded-full bg-blue-500" style={{ width: `${Math.round((taxOptimizedPension / coordinatedPension) * 100)}%` }}></div>
            </div>
          </div>
          
          {/* Koordiniert */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald"></span>
                <span className="text-sm text-slate-600">Koordiniert</span>
              </div>
              <span className="font-semibold tabular-nums text-emerald text-lg">
                {formatCurrency(coordinatedPension)} €/M
              </span>
            </div>
            <div className="w-full h-4 bg-slate-200 rounded-full">
              <div className="h-4 rounded-full bg-emerald" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* The Aha Highlight - Combined Difference */}
      <motion.div 
        className="bg-gradient-to-r from-navy via-blue-900 to-navy rounded-3xl p-8 md:p-10 text-white relative overflow-hidden"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-emerald/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl" />
        
        <div className="relative">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Sparkles className="w-6 h-6 text-amber-400" />
            <span className="text-amber-400 font-semibold uppercase tracking-wider text-sm">Ihr Optimierungspotenzial</span>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 text-center">
            {/* Capital Difference */}
            <div>
              <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Mehr Endkapital</p>
              <p className="font-outfit text-4xl md:text-5xl font-bold text-emerald-400">
                +{formatCurrency(capitalDifference)} €
              </p>
            </div>
            
            {/* Pension Difference */}
            <div>
              <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Mehr Rente monatlich</p>
              <p className="font-outfit text-4xl md:text-5xl font-bold text-amber-400">
                +{formatCurrency(monthlyDifference)} €
              </p>
            </div>
          </div>
          
          <p className="text-white/70 text-center mt-6 text-lg">
            Mit einer koordinierten Strategie – bei <strong className="text-white">gleicher Sparrate</strong>.
          </p>
        </div>
      </motion.div>
    </motion.section>
  );
};

// ============================================
// SECTION 3.5: Financial Foundations (Notgroschen, Absicherung, Immobilie)
// ============================================
const FinancialFoundationsSection = ({ hasNotgroschen, hasAbsicherung, hasImmobilie }) => {
  const foundations = [
    {
      id: 'notgroschen',
      name: 'Rücklagen',
      icon: Wallet,
      isActive: hasNotgroschen,
      activeTitle: 'Liquiditätsreserve vorhanden',
      activeDescription: 'Sie haben eine Notreserve angelegt. Prüfen Sie regelmäßig, ob die Höhe noch zu Ihrer Lebenssituation passt (Empfehlung: 3-6 Monatsgehälter).',
      activeAction: 'Höhe & Anlageform optimieren',
      inactiveTitle: 'Keine Liquiditätsreserve',
      inactiveDescription: 'Rücklagen schützen Sie vor unerwarteten Ausgaben und verhindern, dass Sie Investments vorzeitig auflösen müssen.',
      inactiveAction: 'Warum Rücklagen wichtig sind',
      color: 'amber',
    },
    {
      id: 'absicherung',
      name: 'Absicherung',
      icon: Shield,
      isActive: hasAbsicherung,
      activeTitle: 'Einkommensschutz vorhanden',
      activeDescription: 'Sie haben angegeben, abgesichert zu sein. Lassen Sie Ihre bestehenden Versicherungen auf Kosten, Leistung und Aktualität prüfen.',
      activeAction: 'Kosten & Leistung prüfen lassen',
      inactiveTitle: 'Kein Einkommensschutz',
      inactiveDescription: 'Ohne Berufsunfähigkeitsversicherung riskieren Sie bei Krankheit Ihre gesamte Finanzplanung. Die gesetzliche Absicherung reicht oft nicht aus.',
      inactiveAction: 'Absicherungsbedarf ermitteln',
      color: 'blue',
    },
    {
      id: 'immobilie',
      name: 'Immobilie',
      icon: Home,
      isActive: hasImmobilie,
      activeTitle: 'Immobilie in der Planung',
      activeDescription: 'Eine Immobilie kann Ihre Strategie sinnvoll ergänzen. Wichtig ist die Abstimmung mit Ihrem Gesamtvermögen und Ihre Liquiditätsplanung.',
      activeAction: 'Finanzierungsoptionen besprechen',
      inactiveTitle: 'Keine Immobilie geplant',
      inactiveDescription: 'Eigentum kann langfristig Mietkosten sparen und Vermögen aufbauen. Ob es für Sie sinnvoll ist, hängt von vielen Faktoren ab.',
      inactiveAction: 'Immobilie als Baustein prüfen',
      color: 'emerald',
    },
  ];

  const colorClasses = {
    amber: {
      activeBg: 'bg-amber-50',
      activeBorder: 'border-amber-200',
      activeIcon: 'bg-amber-100 text-amber-600',
      activeText: 'text-amber-700',
      inactiveBg: 'bg-amber-50/50',
      inactiveBorder: 'border-amber-100',
      inactiveIcon: 'bg-amber-100/50 text-amber-500',
    },
    blue: {
      activeBg: 'bg-blue-50',
      activeBorder: 'border-blue-200',
      activeIcon: 'bg-blue-100 text-blue-600',
      activeText: 'text-blue-700',
      inactiveBg: 'bg-blue-50/50',
      inactiveBorder: 'border-blue-100',
      inactiveIcon: 'bg-blue-100/50 text-blue-500',
    },
    emerald: {
      activeBg: 'bg-emerald-50',
      activeBorder: 'border-emerald-200',
      activeIcon: 'bg-emerald-100 text-emerald-600',
      activeText: 'text-emerald-700',
      inactiveBg: 'bg-emerald-50/50',
      inactiveBorder: 'border-emerald-100',
      inactiveIcon: 'bg-emerald-100/50 text-emerald-500',
    },
  };

  return (
    <motion.section 
      className="py-12 md:py-16"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: "-100px" }}
      variants={fadeInUp}
      data-testid="financial-foundations-section"
    >
      <div className="text-center mb-10">
        <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-navy mb-4">
          Ihre Finanzbausteine im Check
        </h2>
        <p className="text-slate-500 text-lg">
          Individuelle Empfehlungen basierend auf Ihrer aktuellen Situation
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {foundations.map((item, index) => {
          const Icon = item.icon;
          const colors = colorClasses[item.color];
          
          return (
            <motion.div
              key={item.id}
              className={`rounded-2xl p-6 border-2 transition-all duration-300 ${
                item.isActive 
                  ? `${colors.activeBg} ${colors.activeBorder}` 
                  : `${colors.inactiveBg} ${colors.inactiveBorder}`
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`foundation-card-${item.id}`}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  item.isActive ? colors.activeIcon : colors.inactiveIcon
                }`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                    {item.name}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5">
                    {item.isActive ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className={`text-sm font-semibold ${item.isActive ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {item.isActive ? 'Vorhanden' : 'Nicht vorhanden'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <h3 className={`font-semibold text-navy mb-2 ${item.isActive ? colors.activeText : ''}`}>
                {item.isActive ? item.activeTitle : item.inactiveTitle}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                {item.isActive ? item.activeDescription : item.inactiveDescription}
              </p>

              {/* Action link to booking page */}
              <Link 
                to="/strategiegespraech"
                className={`inline-flex items-center gap-2 text-sm font-medium transition-all hover:gap-3 ${
                  item.isActive ? colors.activeText : 'text-slate-600 hover:text-navy'
                }`}
                data-testid={`foundation-link-${item.id}`}
              >
                <ArrowRight className="w-4 h-4" />
                <span className="underline underline-offset-2">{item.isActive ? item.activeAction : item.inactiveAction}</span>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Summary callout */}
      <motion.div 
        className="mt-8 p-6 bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl border border-slate-200"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-navy rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-navy mb-1">Ganzheitliche Beratung</h4>
            <p className="text-sm text-slate-600">
              {hasNotgroschen && hasAbsicherung && hasImmobilie 
                ? 'Sie haben alle drei Bausteine berücksichtigt. Im Strategiegespräch optimieren wir das Zusammenspiel für maximale Effizienz.'
                : hasNotgroschen || hasAbsicherung || hasImmobilie
                  ? 'Einige Bausteine sind bereits vorhanden. Im Strategiegespräch identifizieren wir Lücken und Optimierungspotenzial.'
                  : 'Keiner der drei Bausteine ist aktuell aktiv. Das bietet die Chance, von Anfang an eine koordinierte Strategie aufzubauen.'
              }
            </p>
          </div>
        </div>
      </motion.div>
    </motion.section>
  );
};

// ============================================
// SECTION 4: Capital Development Chart
// ============================================
const CapitalDevelopmentSection = ({ projectionData, yearsToRetirement, initialAssets, monthlySavings }) => {
  // Generate chart data for 3 scenarios
  // projectionData has format: { month, year (fraction), age, wealth }
  const chartData = [];
  const currentYear = new Date().getFullYear();
  const totalMonths = yearsToRetirement * 12;
  
  // Sample every ~2 years for cleaner chart
  const sampleInterval = Math.max(24, Math.floor(totalMonths / 15));
  
  for (let month = 0; month <= totalMonths; month += sampleInterval) {
    const dataPoint = projectionData.find(d => d.month === month) || projectionData[Math.min(month, projectionData.length - 1)];
    const progress = month / totalMonths;
    const yearIndex = Math.floor(month / 12);
    const wealth = dataPoint?.wealth || 0;
    
    // Generate label
    let label;
    if (month === 0) {
      label = 'Heute';
    } else if (month >= totalMonths - 12) {
      label = 'Rente';
    } else {
      label = `${currentYear + yearIndex}`;
    }
    
    chartData.push({
      label,
      current: Math.round(wealth),
      optimized: Math.round(wealth * (1 + 0.22 * progress)),
      coordinated: Math.round(wealth * (1 + 0.45 * progress)),
    });
  }
  
  // Ensure we have the final point
  if (chartData.length > 0 && chartData[chartData.length - 1].label !== 'Rente') {
    const finalData = projectionData[projectionData.length - 1];
    if (finalData) {
      chartData.push({
        label: 'Rente',
        current: Math.round(finalData.wealth),
        optimized: Math.round(finalData.wealth * 1.22),
        coordinated: Math.round(finalData.wealth * 1.45),
      });
    }
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-slate-100 p-4">
        <p className="text-sm font-medium text-slate-500 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-600">{entry.name}:</span>
            <span className="font-semibold text-navy">{formatCurrency(entry.value)} €</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <motion.section 
      className="py-12 md:py-16"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: "-100px" }}
      variants={fadeInUp}
      data-testid="capital-development-section"
    >
      <div className="text-center mb-10">
        <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-navy mb-4">
          Kapitalentwicklung über die Zeit
        </h2>
        <p className="text-slate-500 text-lg">
          So entwickelt sich Ihr Vermögen bis zur Rente
        </p>
      </div>
      
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 md:p-10 border border-slate-100">
        <div className="h-[350px] md:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
              <defs>
                <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.slate} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.slate} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorOptimized" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCoordinated" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.emerald} stopOpacity={0.5} />
                  <stop offset="95%" stopColor={COLORS.emerald} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="label" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748B', fontSize: 12 }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748B', fontSize: 12 }}
                tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="current"
                name="Heutige Strategie"
                stroke={COLORS.slate}
                strokeWidth={2}
                fill="url(#colorCurrent)"
              />
              <Area
                type="monotone"
                dataKey="optimized"
                name="Steueroptimiert"
                stroke={COLORS.blue}
                strokeWidth={2}
                fill="url(#colorOptimized)"
              />
              <Area
                type="monotone"
                dataKey="coordinated"
                name="Koordiniert"
                stroke={COLORS.emerald}
                strokeWidth={3}
                fill="url(#colorCoordinated)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-6 mt-6 pt-6 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 rounded-full" style={{ backgroundColor: COLORS.slate }} />
            <span className="text-sm text-slate-600">Heutige Strategie</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 rounded-full" style={{ backgroundColor: COLORS.blue }} />
            <span className="text-sm text-slate-600">Steueroptimiert</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 rounded-full" style={{ backgroundColor: COLORS.emerald }} />
            <span className="text-sm text-slate-600 font-medium">Koordiniert</span>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

// ============================================
// SECTION 5: Optional Exploration
// ============================================
const ExplorationSection = ({ 
  currentSavingsRate, 
  currentCapital, 
  currentPension, 
  yearsToRetirement,
  initialAssets,
  gesetzlicheRente,
  privateRente 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <motion.section 
      className="py-16 md:py-20"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: "-100px" }}
      variants={fadeInUp}
      data-testid="exploration-section"
    >
      <div className="text-center mb-8">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-navy transition-colors"
        >
          <span className="text-lg font-medium">Weitere Analysen erkunden</span>
          <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <WhatIfSlider
            currentSavingsRate={currentSavingsRate}
            currentCapital={currentCapital}
            currentPension={currentPension}
            yearsToRetirement={yearsToRetirement}
            initialAssets={initialAssets}
            gesetzlicheRente={gesetzlicheRente}
            privateRente={privateRente}
          />
        </motion.div>
      )}
    </motion.section>
  );
};

// ============================================
// CALL TO ACTION
// ============================================
const CTASection = ({ data, inputData, onShowLeadCapture, showLeadCapture }) => {
  const navigate = useNavigate();

  if (showLeadCapture) {
    return <LeadCaptureForm analysisData={data} />;
  }

  return (
    <motion.section 
      className="py-12 md:py-16"
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: "-100px" }}
      variants={fadeInUp}
    >
      <div className="bg-gradient-to-br from-navy via-blue-900 to-navy rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
        {/* Decorative glows */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-emerald/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-blue-500/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl" />
        
        <div className="relative">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
            Bereit für den nächsten Schritt?
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-xl mx-auto">
            Lassen Sie uns gemeinsam Ihre persönliche Finanzstrategie entwickeln.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/strategiegespraech')}
              className="px-8 py-4 bg-gradient-to-r from-emerald to-teal-400 text-white rounded-full font-semibold text-lg hover:shadow-lg hover:shadow-emerald/30 transition-all inline-flex items-center gap-2"
              data-testid="cta-booking-btn"
            >
              Strategiegespräch vereinbaren
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => onShowLeadCapture(true)}
              className="px-6 py-4 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-all inline-flex items-center gap-2 border border-white/20"
              data-testid="show-lead-form-btn"
            >
              Analyse per E-Mail erhalten
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export const ResultsDashboard = ({ data, onReset, onRecalculate, inputData }) => {
  const [showLeadCapture, setShowLeadCapture] = useState(false);

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24">
        <div className="bg-white rounded-2xl p-12 text-center">
          <p className="text-slate-500">Analyse wird geladen...</p>
        </div>
      </div>
    );
  }

  // Extract data from API response
  const analysis = data.retirement_gap_analysis || {};
  const etf = data.etf_scenario || {};
  
  // Core values from backend
  const totalPension = analysis.total_retirement_income_monthly || 0;
  const projectedCapital = analysis.projected_capital_at_retirement || 0;
  const targetPension = analysis.desired_retirement_income_monthly || 0;
  const achievementPercentage = analysis.achievement_percentage || 0;
  const yearsToRetirement = analysis.years_to_retirement || 30;
  const gesetzlicheRente = analysis.statutory_pension_net_monthly || 0;
  const privateRente = analysis.private_retirement_income_monthly || 0;
  
  // Calculate strategy scenarios (simple multipliers based on backend capital)
  const taxOptimizedPension = Math.round(totalPension * 1.16);
  const coordinatedPension = Math.round(totalPension * 1.32);
  
  // Projection data for chart
  const projectionData = etf.monthly_series || analysis.projection_series || [];

  return (
    <div className="bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="results-dashboard">
      <div className="max-w-4xl mx-auto px-6 py-8">
        
        {/* SECTION 1: Hero - Current Result */}
        <HeroSection 
          totalPension={totalPension}
          projectedCapital={projectedCapital}
        />
        
        {/* SECTION 2: Target Comparison */}
        <TargetComparisonSection
          targetPension={targetPension}
          currentPension={totalPension}
          achievementPercentage={achievementPercentage}
        />
        
        {/* SECTION 3: Strategy Potential (AHA MOMENT) */}
        <StrategyPotentialSection
          currentPension={totalPension}
          taxOptimizedPension={taxOptimizedPension}
          coordinatedPension={coordinatedPension}
          currentCapital={projectedCapital}
        />

        {/* SECTION 3.5: Financial Foundations Check */}
        <FinancialFoundationsSection
          hasNotgroschen={inputData?.has_notgroschen || false}
          hasAbsicherung={inputData?.has_income_protection || false}
          hasImmobilie={inputData?.has_real_estate || false}
        />
        
        {/* SECTION 4: Capital Development Chart */}
        <CapitalDevelopmentSection
          projectionData={projectionData}
          yearsToRetirement={yearsToRetirement}
        />
        
        {/* SECTION 5: Optional Exploration */}
        <ExplorationSection
          currentSavingsRate={inputData?.monthly_savings || 500}
          currentCapital={projectedCapital}
          currentPension={totalPension}
          yearsToRetirement={yearsToRetirement}
          initialAssets={inputData?.current_investable_assets || 0}
          gesetzlicheRente={gesetzlicheRente}
          privateRente={privateRente}
        />
        
        {/* CTA Section */}
        <CTASection 
          data={data}
          inputData={inputData}
          onShowLeadCapture={setShowLeadCapture}
          showLeadCapture={showLeadCapture}
        />
        
        {/* Disclaimer */}
        <div className="py-12 text-center">
          <p className="text-sm text-slate-400 max-w-2xl mx-auto">
            <strong>Hinweis:</strong> Diese Analyse basiert auf Szenarien und konfigurierbaren Annahmen. 
            Keine Garantie für zukünftige Entwicklungen. Individuelle Beratung empfohlen.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResultsDashboard;
